import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { Formik } from "formik";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MANAGE_CONSENT_FORM } from "config/paths";
import addContentSchema from "./addContent.schema";
import { CLIENT_TYPE_OPTIONS, CONSENT_STATUS_OPTIONS } from "config/constants";
import { CommonSelect, RequiredFieldAstrick } from "components/Common/Common";
import { successMessageToast } from "helpers/common";
import { useCreateConsentFormMutation, useGetConsentFormDetailsQuery, useUpdateConsentFormMutation } from "store/services/consent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { cloneDeep } from "lodash";
import CKEditorField from "components/CkEditor/CkEditor";

const AddConsent = () => {

  const { id: contentId } = useParams();
  const editMode = !!contentId;
  const navigate = useNavigate();

  const { data: consentFormDetails, isLoading } = useGetConsentFormDetailsQuery(contentId, { skip: !contentId });

  const [initialValues, setInitialValues] = useState({ title: "", status: "active", description: "", consent_form: "", consent_for: "" });
  const [createConsentForm, { isLoading: createConsentFormLoading }] = useCreateConsentFormMutation();
  const [updateConsentForm, { isLoading: updateConsentFormLoading }] = useUpdateConsentFormMutation();

  const handleSubmit = async (values) => {
    try {
      const payload = cloneDeep(values);
      payload['consent_for'] = values?.consent_for?.value;

      const result = editMode ? await updateConsentForm({ id: contentId, data: payload }) : await createConsentForm(payload);
      if (result.error) return;

      successMessageToast(result?.data?.message);
      navigate(MANAGE_CONSENT_FORM)
    } catch (err) { console.log("Error creating/editing concent form", err) };
  };

  useEffect(() => {

    if (!contentId) return;

    if (consentFormDetails) {

      setInitialValues({
        ...initialValues,
        title: consentFormDetails?.title || "",
        status: consentFormDetails?.status || "",
        description: consentFormDetails?.description || "",
        consent_form: consentFormDetails?.consent_form || "",
        consent_for: CLIENT_TYPE_OPTIONS.find(x => x.value == consentFormDetails?.consent_for) || ""
      });
    };

  }, [consentFormDetails]);

  return (
    <>
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          {isLoading ? <LoadingIndicator /> : <Row>
            <Col lg="12" className="my-2">
              <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                <div className="left">
                  <div className="pageHead pb-3 d-flex align-items-center">
                    <Link
                      className="themeBlue fw-sbold back-to-btn"
                      to="/cms/"
                    >
                      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                      </svg> Back
                    </Link>
                    <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Page" : "Add New Page"}</h6>
                  </div>
                </div>
              </div>

            </Col>
            <Col lg="12" className="my-2">
              <Formik
                initialValues={initialValues}
                validationSchema={addContentSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({
                  handleSubmit,
                  values,
                  setFieldValue,
                  errors
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <div
                      className="py-3"
                      style={{ borderTop: "1px solid #41a2a84d" }}
                    >
                      <Row>
                        <Col md="6" sm="6" className="my-2">
                          <label
                            htmlFor=""
                            className="form-label themeBlue fw-bold mb-0 d-flex"
                          >
                            Title
                            <RequiredFieldAstrick />
                          </label>
                          <AppInputField
                            type="text"
                            name="title"
                            placeholder="Enter Title"
                            className={`${styles.formControl} form-control`}
                            maxLength={150}
                          />
                        </Col>
                        <Col md="6" sm="6" className="my-2">
                          <label
                            htmlFor=""
                            className="form-label themeBlue fw-bold mb-0 d-flex"
                          >
                            Status
                            <RequiredFieldAstrick />
                          </label>
                          <AppSelectField
                            name={"status"}
                            placeholder={"Please Select"}
                            className={`${styles.formControl} form-control`}
                            options={CONSENT_STATUS_OPTIONS}
                          />
                        </Col>
                        <Col md="12" sm="12" className="my-2">
                          <label
                            htmlFor=""
                            className="form-label themeBlue fw-bold mb-0 d-flex"
                          >
                            Description
                            <RequiredFieldAstrick />
                          </label>
                          <AppInputField
                            type="text"
                            fieldType={"textarea"}
                            name="description"
                            placeholder="Enter Description"
                            className={`${styles.formControl} form-control`}
                            maxLength={500}
                          />
                        </Col>
                        <Col md="6" sm="6" className="my-2">
                          <label
                            htmlFor=""
                            className="form-label themeBlue fw-bold mb-0 d-flex"
                          >
                            Consent For
                            <RequiredFieldAstrick />
                          </label>
                          <CommonSelect
                            name={"consent_for"}
                            placeholder={"Please Select"}
                            className={`${styles.formControl}`}
                            options={CLIENT_TYPE_OPTIONS}
                          />
                        </Col>
                        <Col md="12" sm="12" className="my-2">
                          <label
                            htmlFor=""
                            className="form-label themeBlue fw-bold mb-0 d-flex"
                          >
                            Consent Form
                            <RequiredFieldAstrick />
                          </label>
                          <div className={`ck-editor-wrapper`} >
                            <CKEditorField
                              handleChange={(event, editor) => setFieldValue("consent_form", editor?.getData())}
                              value={values.consent_form}
                            />
                          </div>
                          {errors?.consent_form ? <div className="error">{errors?.consent_form}</div> : null}
                        </Col>
                      </Row>
                    </div>

                    <Row className="mt-4">
                      <Col className="my-2" lg="12">
                        <div
                          className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          <Button
                            type="submit"
                            className="d-flex align-items-center justify-content-center commonBtn"
                            disabled={createConsentFormLoading || updateConsentFormLoading}
                          >
                            {createConsentFormLoading || updateConsentFormLoading ? <LoadingIndicator inline /> : "Save"}
                          </Button>
                          <Button
                            type="button"
                            className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                            onClick={() => navigate(MANAGE_CONSENT_FORM)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>}
        </Container>
      </section>
    </>
  );
};

export default AddConsent;

const singleDocument = {
  title: "",
  description: "",
  attachment: {
    attachment_name: "",
    attachment_key: ""
  }
}